import { Loader2, Inbox, Search } from 'lucide-react';
import { statusColor } from '../models';

const COLOR_CLASSES = {
  orange: 'bg-orange-100 text-orange-700',
  blue: 'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700',
  green: 'bg-green-100 text-green-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  teal: 'bg-teal-100 text-teal-700',
  red: 'bg-red-100 text-red-700',
  gray: 'bg-gray-100 text-gray-600',
};

export function StatusBadge({ status }) {
  const c = statusColor(status);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${COLOR_CLASSES[c]}`}>
      {status}
    </span>
  );
}

export function LoadingView({ label = 'Loading…' }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
      <Loader2 className="animate-spin" size={28} />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title = 'Nothing here yet', subtitle = '', action = null }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-2">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-300 mb-1">
        <Icon size={30} />
      </div>
      <p className="font-semibold text-gray-700">{title}</p>
      {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
      {action}
    </div>
  );
}

export function SearchBar({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-3 py-2 rounded-xl bg-gray-100 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40"
      />
    </div>
  );
}

// Horizontal scrollable filter chips (iOS status filter tabs).
export function FilterChips({ options, value, onChange }) {
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar px-1 py-1">
      {options.map(opt => {
        const v = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const count = typeof opt === 'object' ? opt.count : undefined;
        const active = value === v;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              active ? 'bg-[#FF6B35] text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {label}{count !== undefined ? ` (${count})` : ''}
          </button>
        );
      })}
    </div>
  );
}

// iOS segmented picker (Day/Week/Month/Year).
export function SegmentedPicker({ options, value, onChange }) {
  return (
    <div className="flex bg-gray-100 rounded-xl p-1">
      {options.map(opt => {
        const v = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              value === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function StatCard({ label, value, sub = null, icon: Icon = null, color = 'text-gray-900' }) {
  return (
    <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
      <div className="flex items-center gap-1.5 text-gray-400 text-[11px] font-medium">
        {Icon && <Icon size={13} />}
        {label}
      </div>
      <div className={`text-lg font-extrabold mt-0.5 ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

// iOS-style settings list row.
export function ListRow({ icon: Icon = null, label, value = null, onClick = null, destructive = false, chevron = true }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick || undefined}
      className={`w-full flex items-center gap-3 px-4 py-3.5 bg-white text-left ${
        onClick ? 'active:bg-gray-50' : ''
      }`}
    >
      {Icon && (
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          destructive ? 'bg-red-50 text-red-500' : 'bg-orange-50 text-[#FF6B35]'
        }`}>
          <Icon size={17} />
        </span>
      )}
      <span className={`flex-1 text-sm font-medium ${destructive ? 'text-red-500' : 'text-gray-800'}`}>
        {label}
      </span>
      {value && <span className="text-sm text-gray-400">{value}</span>}
      {onClick && chevron && <span className="text-gray-300">›</span>}
    </Tag>
  );
}

// iOS-style toggle switch.
export function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-7 rounded-full transition-colors ${
        checked ? 'bg-green-500' : 'bg-gray-300'
      } ${disabled ? 'opacity-50' : ''}`}
    >
      <span
        className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${
          checked ? 'left-[22px]' : 'left-0.5'
        }`}
      />
    </button>
  );
}

export function PrimaryButton({ children, onClick, disabled = false, color = 'bg-[#FF6B35]', className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full py-3 rounded-2xl text-white font-bold text-sm shadow-sm disabled:opacity-50 active:scale-[0.98] transition-transform ${color} ${className}`}
    >
      {children}
    </button>
  );
}

export function Card({ children, className = '', onClick = null }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick || undefined}
      className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${onClick ? 'w-full text-left active:bg-gray-50' : ''} ${className}`}
    >
      {children}
    </Tag>
  );
}
